import { useState, useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import useRefreshToken from "../hooks/useRefreshToken";
import useAuth from "../hooks/useAuth";
import Loader from "./Loader";
import Header from "./Header";
import SideBar from "./SideBar";
import ToastManager from "./ToastManager";
import { useDispatch, useSelector } from "react-redux";
import {
  selectActiveWinery,
  selectLabels,
  selectUserSockets,
  selectWineries,
} from "../context/selectors";
import { setUserSockets } from "../context/userSockets/userSocketsSlice";
import { toast } from "react-toastify";
import useLabels from "../hooks/useLabels";
import { getItem, setItem } from "../services/LocalStorage";
import Footer from "./Footer";
import CookieBar from "./CookieBar";
import { NotificationWithLink } from "./Generic";
import NotesButton from "./FloatingNotes/NotesButton";
import { removeOp } from "../context/operations/operationsSlice";
import { addDays } from "date-fns";
import useLogout from "../hooks/useLogout";
import useRbacSync from "../hooks/useRbacSync";
import { buildLoginRedirectFrom } from "../services/redirect";
import { getWineries } from "../services/utils";
import { set as setWineries, setActive as setActiveWinery } from "../context/wineries/wineriesSlice";

// NEW: Imports for multi-tab sync and cache management
import broadcastChannel from '../services/broadcastChannelService';
import CacheManager from '../services/cacheManager';
import { applyEvent, catchUp } from '../services/syncManager';
import useAxiosPrivate from '../hooks/useAxiosPrivate';

const PersistentLogin = ({ isMobile, socket }) => {
  const [getLabel, getArea, setLabels, ready] = useLabels();

  const logout = useLogout();

  const [isLoading, setIsLoading] = useState(true);
  const refresh = useRefreshToken();
  const { auth, setAuth } = useAuth();
  const dispatch = useDispatch();
  const wineries = useSelector(selectWineries);
  const activeWinery = useSelector(selectActiveWinery);
  const userSockets = useSelector(selectUserSockets);
  const navigate = useNavigate();
  const location = useLocation();

  const [isConnected, setIsConnected] = useState(socket.connected);
  const [isIcon, setIsIcon] = useState(getItem("sidebar_icon") || false);

  // NEW: Get axios instance for CacheManager
  const axiosPrivate = useAxiosPrivate();

  const customIdExpiringPwd = "custom-id-exp-pwd";

  // Fetch RBAC permissions for active winery whenever it changes
  useRbacSync();

  useEffect(() => {
    if (!auth?.accessToken || !activeWinery?.id || !ready) return;

    if (!auth?.password_expire_date) return;
    const expiring =
      addDays(new Date(), 10) > new Date(auth.password_expire_date);
    if (expiring)
      toast.warning(getLabel("toast_WarningIsPasswordExpiring"), {
        toastId: customIdExpiringPwd,
        position: toast.POSITION.BOTTOM_RIGHT,
        exclude: true,
        autoClose: false,
        onClick: () => navigate(`/users/${auth.user_id}/update`),
      });
  }, [auth?.accessToken, auth?.password_expire_date, activeWinery?.id, ready]);

  // Un reload di una sessione già autenticata NON passa da Login/index.js,
  // quindi senza questo effetto la slice wineries (e localStorage) restano
  // ferme ai dati dell'ultimo login: eventuali modifiche CLIENT-side (es.
  // allow_cellarman_show_all_operations, currency) non sono mai viste finché
  // l'utente non fa logout/login. Rifetch qui, come fa Login/index.js.
  useEffect(() => {
    if (!auth?.accessToken) return;
    let isMounted = true;

    (async () => {
      try {
        const freshWineries = await getWineries(axiosPrivate, auth.accessToken, { throwOnError: true });
        if (!isMounted) return;
        dispatch(setWineries(freshWineries));

        // `set` azzera `active` se la lista ha più di una winery: ripristina
        // quella già attiva (per id) per non spostare l'utente dalla winery
        // su cui si trovava prima del reload.
        const currentActiveId = activeWinery?.id ?? getItem("activeWineryId");
        const refreshedActive = freshWineries?.find((w) => w.id === currentActiveId);
        if (refreshedActive) dispatch(setActiveWinery(refreshedActive));
      } catch (err) {
        console.error(err);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [auth?.accessToken]);

  // ✅ FIXED: Complete login verification and navigation logic
  useEffect(() => {
    let isMounted = true;

    const verifyRefreshToken = async () => {
      try {
        await refresh();
      } catch (err) {
        console.error(err);
      } finally {
        // Navigation after refresh
        if (auth?.role === 1) {
          navigate("/clients");
        } else {
          if (!activeWinery) {
            if (!wineries || wineries?.length === 0) {
              navigate("/");
            } else {
              navigate("/#wineriesList");
            }
          } else {
            navigate(location.pathname);
          }
        }
        isMounted && setIsLoading(false);
      }
    };

    if (!auth?.accessToken) {
      // No token, need to refresh
      verifyRefreshToken();
    } else {
      // Token exists, navigate based on role
      if (auth?.role === 1) {
        navigate("/clients");
        setIsLoading(false);
      } else {
        if (!activeWinery && auth?.role === 2) {
          if (!wineries || wineries?.length === 0) {
            navigate("/wineries/new");
            setIsLoading(false);
            const customIdNoWineries = "custom-id-no-wineries";
            toast.info(getLabel("toast_missingWineries"), {
              toastId: customIdNoWineries,
              position: toast.POSITION.BOTTOM_RIGHT,
              exclude: true,
              autoClose: 5000,
            });
          } else {
            navigate("/#wineriesList");
            setIsLoading(false);
          }
        } else {
          navigate(location.pathname);
          setIsLoading(false);
        }
      }
    }

    // ✅ Socket connection with JWT - only after token is available
    if (!auth?.user_id || !activeWinery || !auth?.accessToken) return;

    socket.auth = {
      token: auth?.accessToken,
      wineryId: activeWinery?.id,
    };

    if (!socket.connected) {
      socket.connect();
    }

    return () => {
      isMounted = false;
    };
  }, [auth?.user_id, auth?.accessToken, activeWinery?.id]);

  useEffect(() => {
    const delay = 5000;
    const timeout = setTimeout(() => {
      if (!ready) {
        return;
      }

      // Fetch notifications
      socket.emit("fetch notifications");
    }, delay);

    return () => clearTimeout(timeout);
  }, [ready]);

  // ✅ Socket event handlers
  // ✅ SOLUZIONE: Exponential backoff con limite di retry
  useEffect(() => {
    let retryCount = 0;
    const MAX_RETRIES = 100;
    let retryTimeout = null;

    function onConnect() {
      console.log("✅ Socket connected");
      setIsConnected(true);
      reconnecting = false;
      retryCount = 0; // ✅ Reset counter on success

      // Heal any events missed while the socket was down (covers both the
      // initial connect and every reconnect). Replays from our last cursor or
      // full-resyncs if the server says it's too old.
      if (activeWinery?.id) {
        catchUp({
          dispatch,
          axios: axiosPrivate,
          wineryId: activeWinery.id,
          cacheManager: new CacheManager(dispatch, axiosPrivate),
        });
      }
    }

    function onReconnectFailed() {
      console.log("Reconnection failed");
    }

    function onReconnectAttempt() {
      console.log("Reconnect attempt");

      socket.auth = {
        token: auth?.accessToken,
        wineryId: activeWinery?.id,
      };
    }

    let reconnecting = false;

    function onDisconnect(reason) {
      console.log("disconnect", reason);
      setIsConnected(false);

      if (reason === "io server disconnect" && !reconnecting && auth?.user_id) {
        reconnecting = true;

        socket.auth = {
          token: auth?.accessToken,
          wineryId: activeWinery?.id,
        };

        socket.connect();
      }

      const index = userSockets?.findIndex((u) => u?.self);

      if (index === -1) {
        return;
      }
      dispatch(
        setUserSockets((prevState) => [
          ...prevState?.slice(0, index),
          {
            ...prevState[index],
            connected: false,
            ws: undefined,
          },
          ...prevState?.slice(index + 1),
        ]),
      );
    }

    function onConnectError(err) {
      console.error("❌ onConnectError", err);

      // ✅ Detect token expiration immediately and refresh
      const isTokenExpired = err.message?.includes("jwt expired") ||
                            err.message?.includes("token expired") ||
                            err.message?.includes("Authentication error");

      if (isTokenExpired) {
        // console.log("🔄 Token expired detected, refreshing immediately...");

        if (retryTimeout) clearTimeout(retryTimeout);

        // Refresh token immediately
        refresh()
          .then(() => {
            // console.log("✅ Token refreshed, reconnecting...");

            // Update auth with new token
            socket.auth = {
              token: auth?.accessToken,
              wineryId: activeWinery?.id,
            };

            socket.connect();
          })
          .catch((refreshErr) => {
            console.error("❌ Token refresh failed:", refreshErr);

            // Logout and redirect to login, preservando la pagina corrente per il ritorno post-login
            logout();
            navigate("/login", {
              state: {
                from: buildLoginRedirectFrom(
                  `${window.location.pathname}${window.location.search}`
                ),
              },
            });

            toast.error(getLabel("toast_sessionExpired"), {
              position: toast.POSITION.BOTTOM_RIGHT,
              autoClose: 5000,
            });
          });
        return;
      }

      // Per altri tipi di errori, retry con delay fisso
      if (err.message === "invalid username") {
        console.log("usernameAlreadySelected", err);
      }

      if (retryTimeout) clearTimeout(retryTimeout);

      retryTimeout = setTimeout(() => {
        socket.auth = {
          token: auth?.accessToken,
          wineryId: activeWinery?.id,
        };

        console.log("Retrying connection after error...");
        socket.connect();
      }, 5000);
    }

    function onUsers(users) {
      const userSocketsTmp = [...userSockets];

      for (const user of users) {
        user.notifications?.forEach((notification) => {
          notification.fromSelf = notification.from === socket.userID;
        });

        const userIndex = userSocketsTmp?.findIndex((u) => u?.id === user?.id);
        if (userIndex === -1) {
          user.self = user.userID === socket.userID;
          userSocketsTmp.push(user);
        } else {
          const tmpUser = { ...userSocketsTmp[userIndex], ws: undefined };
          userSocketsTmp[userIndex] = tmpUser;
        }
      }

      userSocketsTmp?.sort((a, b) => {
        if (a.self) return -1;
        if (b.self) return 1;
        if (a.username < b.username) return -1;
        return a.username > b.username ? 1 : 0;
      });

      dispatch(setUserSockets(userSocketsTmp));
    }

    function onUserConnected(user) {
      const userSocketsTmp = [...userSockets];

      const userIndex = userSocketsTmp?.findIndex((u) => u?.id === user?.id);
      if (userIndex === -1) {
        user.self = user.userID === socket.userID;
        user.connected = true;
        userSocketsTmp.push(user);
      } else {
        const tmpUser = { ...userSocketsTmp[userIndex] };
        tmpUser.connected = true;
        userSocketsTmp[userIndex] = tmpUser;
      }
    }

    function onUserDisonnected(id) {
      const userSocketsTmp = [...userSockets];

      const userIndex = userSocketsTmp?.findIndex((u) => u?.id === id);
      if (userIndex > -1) {
        const tmpUser = { ...userSocketsTmp[userIndex] };
        tmpUser.connected = false;
        userSocketsTmp[userIndex] = tmpUser;
      }
    }

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("reconnect_attempt", onReconnectAttempt);
    socket.on("reconnect_failed", onReconnectFailed);
    socket.on("users", onUsers);
    socket.on("user connected", onUserConnected);
    socket.on("user disconnected", onUserDisonnected);
    socket.on("connect_error", onConnectError);

    return () => {
      if (retryTimeout) clearTimeout(retryTimeout); // ✅ Cleanup

      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("reconnect_attempt", onReconnectAttempt);
      socket.off("reconnect_failed", onReconnectFailed);
      socket.off("users", onUsers);
      socket.off("user connected", onUserConnected);
      socket.off("user disconnected", onUserDisonnected);
      socket.off("connect_error", onConnectError);
    };
  }, [
    auth?.accessToken,
    auth?.user_id,
    activeWinery?.id,
    userSockets,
    dispatch,
    axiosPrivate,
  ]);

  // NEW: Listen to cache invalidation from Socket.IO (server-side events from other users)
  useEffect(() => {
    if (!socket || !activeWinery?.id) return;

    const cacheManager = new CacheManager(dispatch, axiosPrivate);

    // Handle cache invalidation from server (other users' actions)
    const handleCacheInvalidate = async (event) => {
      console.log('🔄 Cache invalidation from server:', event);

      if (event.affected_entities) {
        // Apply + advance the sync cursor (seq), so reconnect/focus catch-up
        // knows where we left off.
        await applyEvent(event, { cacheManager, wineryId: activeWinery?.id });

        // Also broadcast to other tabs of same browser (carry seq so they
        // advance their cursor too).
        broadcastChannel.broadcastCacheInvalidation({
          affected_entities: event.affected_entities,
          seq: event.seq,
        });
      }
    };

    socket.on('cache:invalidate', handleCacheInvalidate);

    // NEW: Listen to CACHE_INVALIDATION event with metadata
    const handleCacheInvalidationWithMetadata = async (message) => {
      console.log('🔄 Cache invalidation with metadata from server:', message);

      if (message?.data?.affected_entities) {
        // Process affected entities
        await cacheManager.processAffectedEntities(message.data.affected_entities);

        // Also broadcast to other tabs of same browser
        broadcastChannel.broadcastCacheInvalidation({
          ...message.data.affected_entities,
          metadata: message.data.metadata,
        });
      }
    };

    socket.on('CACHE_INVALIDATION', handleCacheInvalidationWithMetadata);

    return () => {
      socket.off('cache:invalidate', handleCacheInvalidate);
      socket.off('CACHE_INVALIDATION', handleCacheInvalidationWithMetadata);
    };
  }, [socket, activeWinery?.id, dispatch, axiosPrivate]);

  // NEW: Listen to broadcast messages from other tabs
  useEffect(() => {
    const cacheManager = new CacheManager(dispatch, axiosPrivate);

    // Listen to Redux actions from other tabs
    broadcastChannel.on('REDUX_ACTION', (action) => {
      console.log('📡 Received action from another tab:', action.type);
      dispatch(action); // Dispatch to local Redux store
    });

    // Listen to cache invalidation from other tabs. New shape is
    // { affected_entities, seq }; tolerate the legacy bare affected_entities too.
    broadcastChannel.on('CACHE_INVALIDATE', async (payload) => {
      console.log('🔄 Cache invalidation from another tab:', payload);
      const event = payload?.affected_entities
        ? payload
        : { affected_entities: payload };
      await applyEvent(event, { cacheManager, wineryId: activeWinery?.id });
    });

    return () => {
      // Cleanup listeners only. Do NOT close() the app-lifetime singleton channel:
      // close() nulls the native channel with no re-open, killing multi-tab sync until
      // a full page reload (F5). The channel is closed on real logout/unload instead.
      broadcastChannel.off('REDUX_ACTION');
      broadcastChannel.off('CACHE_INVALIDATE');
    };
  }, [dispatch, axiosPrivate, activeWinery?.id]);

  // NEW: Refetch-on-focus safety net. A user looking at a stale cellar view who
  // missed a socket event (or whose tab was backgrounded) gets reconciled when
  // the tab becomes visible again. Only fires after being hidden a few seconds
  // to avoid churn on quick tab switches. No blind polling.
  useEffect(() => {
    if (!activeWinery?.id) return;

    const HIDDEN_THRESHOLD_MS = 5000;
    let hiddenSince = null;

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenSince = Date.now();
        return;
      }
      // becoming visible
      const wasHiddenLongEnough =
        hiddenSince === null || Date.now() - hiddenSince >= HIDDEN_THRESHOLD_MS;
      hiddenSince = null;
      if (!wasHiddenLongEnough) return;

      catchUp({
        dispatch,
        axios: axiosPrivate,
        wineryId: activeWinery.id,
        cacheManager: new CacheManager(dispatch, axiosPrivate),
      });
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [dispatch, axiosPrivate, activeWinery?.id]);

  // useEffect(() => {
  //   function onConnect() {
  //     console.log("✅ Socket connected");
  //     setIsConnected(true);
  //     reconnecting = false;
  //   }

  //   function onReconnectFailed() {
  //     console.log("Reconnection failed");
  //   }

  //   function onReconnectAttempt() {
  //     console.log("Reconnect attempt");

  //     // Update with current token
  //     socket.auth = {
  //       token: auth?.accessToken,
  //       wineryId: activeWinery?.id,
  //     };
  //   }

  //   let reconnecting = false;

  //   function onDisconnect(reason) {
  //     console.log("disconnect", reason);
  //     setIsConnected(false);

  //     if (reason === "io server disconnect" && !reconnecting && auth?.user_id) {
  //       reconnecting = true;

  //       socket.auth = {
  //         token: auth?.accessToken,
  //         wineryId: activeWinery?.id,
  //       };

  //       socket.connect();
  //     }

  //     const index = userSockets?.findIndex((u) => u?.self);

  //     if (index === -1) {
  //       return;
  //     }
  //     dispatch(
  //       setUserSockets((prevState) => [
  //         ...prevState?.slice(0, index),
  //         {
  //           ...prevState[index],
  //           connected: false,
  //           ws: undefined,
  //         },
  //         ...prevState?.slice(index + 1),
  //       ])
  //     );
  //   }

  //   function onConnectError(err) {
  //     console.error("❌ onConnectError", err);

  //     // ✅ Se è un errore di autenticazione, NON fare retry automatico
  //     // Il token verrà refreshato dal meccanismo esistente (useRefreshToken)
  //     if (err.message === "Authentication error") {
  //       console.error("❌ JWT authentication failed - waiting for token refresh");
  //       // Non fare nulla - il refresh avverrà automaticamente
  //       return;
  //     }

  //     // Per altri tipi di errori, retry dopo 5 secondi
  //     if (err.message === "invalid username") {
  //       console.log("usernameAlreadySelected", err);
  //     }

  //     setTimeout(() => {
  //       socket.auth = {
  //         token: auth?.accessToken,
  //         wineryId: activeWinery?.id,
  //       };

  //       console.log("Retrying connection after error...");
  //       socket.connect();
  //     }, 5000);
  //   }

  //   function onUsers(users) {
  //     const userSocketsTmp = [...userSockets];

  //     for (const user of users) {
  //       user.notifications?.forEach((notification) => {
  //         notification.fromSelf = notification.from === socket.userID;
  //       });

  //       const userIndex = userSocketsTmp?.findIndex((u) => u?.id === user?.id);
  //       if (userIndex === -1) {
  //         user.self = user.userID === socket.userID;
  //         userSocketsTmp.push(user);
  //       } else {
  //         const tmpUser = { ...userSocketsTmp[userIndex], ws: undefined };
  //         userSocketsTmp[userIndex] = tmpUser;
  //       }
  //     }

  //     userSocketsTmp?.sort((a, b) => {
  //       if (a.self) return -1;
  //       if (b.self) return 1;
  //       if (a.username < b.username) return -1;
  //       return a.username > b.username ? 1 : 0;
  //     });

  //     dispatch(setUserSockets(userSocketsTmp));
  //   }

  //   function onUserConnected(user) {
  //     const userSocketsTmp = [...userSockets];

  //     const userIndex = userSocketsTmp?.findIndex((u) => u?.id === user?.id);
  //     if (userIndex === -1) {
  //       user.self = user.userID === socket.userID;
  //       user.connected = true;
  //       userSocketsTmp.push(user);
  //     } else {
  //       const tmpUser = { ...userSocketsTmp[userIndex] };
  //       tmpUser.connected = true;
  //       userSocketsTmp[userIndex] = tmpUser;
  //     }
  //   }

  //   function onUserDisonnected(id) {
  //     const userSocketsTmp = [...userSockets];

  //     const userIndex = userSocketsTmp?.findIndex((u) => u?.id === id);
  //     if (userIndex > -1) {
  //       const tmpUser = { ...userSocketsTmp[userIndex] };
  //       tmpUser.connected = false;
  //       userSocketsTmp[userIndex] = tmpUser;
  //     }
  //   }

  //   socket.on("connect", onConnect);
  //   socket.on("disconnect", onDisconnect);
  //   socket.on("reconnect_attempt", onReconnectAttempt);
  //   socket.on("reconnect_failed", onReconnectFailed);
  //   socket.on("users", onUsers);
  //   socket.on("user connected", onUserConnected);
  //   socket.on("user disconnected", onUserDisonnected);
  //   socket.on("connect_error", onConnectError);

  //   return () => {
  //     socket.off("connect", onConnect);
  //     socket.off("disconnect", onDisconnect);
  //     socket.off("reconnect_attempt", onReconnectAttempt);
  //     socket.off("reconnect_failed", onReconnectFailed);
  //     socket.off("users", onUsers);
  //     socket.off("user connected", onUserConnected);
  //     socket.off("user disconnected", onUserDisonnected);
  //     socket.off("connect_error", onConnectError);
  //   };
  // }, [auth?.accessToken, auth?.user_id, activeWinery?.id, userSockets, dispatch]);

  return (
    <>
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <Header isMobile={isMobile} isConnected={isConnected}></Header>
          <main className={`container ${isIcon ? "icon" : ""}`}>
            <SideBar
              isMobile={isMobile}
              isIcon={isIcon}
              setIsIcon={setIsIcon}
            ></SideBar>
            <ToastManager />
            <NotesButton />
            <Outlet />
          </main>
          <Footer isMobile={isMobile}></Footer>
          <CookieBar isMobile={isMobile}></CookieBar>
        </>
      )}
    </>
  );
};

export default PersistentLogin;

// import { useState, useEffect } from "react";
// import { Outlet, useLocation, useNavigate } from "react-router-dom";
// import useRefreshToken from "../hooks/useRefreshToken";
// import useAuth from "../hooks/useAuth";
// import Loader from "./Loader";
// import Header from "./Header";
// import SideBar from "./SideBar";
// import ToastManager from "./ToastManager";
// import { useDispatch, useSelector } from "react-redux";
// import {
//   selectActiveWinery,
//   selectLabels,
//   selectUserSockets,
//   selectWineries,
// } from "../context/selectors";
// import { setUserSockets } from "../context/userSockets/userSocketsSlice";
// import { toast } from "react-toastify";
// import useLabels from "../hooks/useLabels";
// import { getItem, setItem } from "../services/LocalStorage";
// import Footer from "./Footer";
// import CookieBar from "./CookieBar";
// import { NotificationWithLink } from "./Generic";
// import NotesButton from "./FloatingNotes/NotesButton";
// import { removeOp } from "../context/operations/operationsSlice";
// import { addDays } from "date-fns";

// const PersistentLogin = ({ isMobile, socket }) => {
//   // const [getLabel, ready] = useLabels();
//   const [getLabel, getArea, setLabels, ready] = useLabels();

//   const [isLoading, setIsLoading] = useState(true);
//   const refresh = useRefreshToken();
//   const { auth, setAuth } = useAuth();
//   const dispatch = useDispatch();
//   const wineries = useSelector(selectWineries);
//   const activeWinery = useSelector(selectActiveWinery);
//   const userSockets = useSelector(selectUserSockets);
//   // const isLabelsLoaded = useSelector(selectLabels);
//   const navigate = useNavigate();
//   const location = useLocation();
//   // socket.io
//   const [isConnected, setIsConnected] = useState(socket.connected);
//   const [isIcon, setIsIcon] = useState(getItem("sidebar_icon") || false);

//   const customIdExpiringPwd = "custom-id-exp-pwd";
//   useEffect(() => {
//     if (
//       !auth?.accessToken ||
//       !activeWinery?.id ||
//       !ready
//     ) return;

//     if (!auth?.password_expire_date) return;
//     const expiring = addDays(new Date(), 10) > new Date(auth.password_expire_date);
//     if (expiring)
//       toast.warning(getLabel("toast_WarningIsPasswordExpiring"), {
//         toastId: customIdExpiringPwd,
//         position: toast.POSITION.BOTTOM_RIGHT,
//         exclude: true,
//         autoClose: false,
//         onClick: () => navigate(`/users/${auth.user_id}/update`),
//       });
//   }, [
//     auth?.accessToken,
//     auth?.password_expire_date,
//     activeWinery?.id,
//     ready
//   ]);

//   // useEffect(() => {
//   //   let isMounted = true;

//   //   const verifyRefreshToken = async () => {
//   //     try {
//   //       await refresh();
//   //     } catch (err) {
//   //       console.error(err);
//   //     } finally {
//   //       if (auth?.role === 1) {
//   //         navigate("/clients");
//   //         setIsLoading(false);
//   //       } else {
//   //         if (!activeWinery) {
//   //           if (!wineries || wineries?.length === 0) {
//   //             navigate("/");
//   //             // navigate('/wineries/new');
//   //             setIsLoading(false);
//   //             return;
//   //           }
//   //           navigate("/#wineriesList");
//   //         } else {
//   //           navigate(location.pathname);
//   //         }
//   //       }
//   //       isMounted && setIsLoading(false);
//   //     }
//   //   };

//   //   if (!auth?.accessToken) {
//   //     verifyRefreshToken();
//   //   } else {
//   //     if (auth?.role === 1) {
//   //       navigate("/clients");
//   //       setIsLoading(false);
//   //     } else {
//   //       if (!activeWinery && auth?.role === 2) {
//   //         if (!wineries || wineries?.length === 0) {
//   //           navigate("/wineries/new");
//   //           setIsLoading(false);
//   //           const customIdNoWineries = "custom-id-no-wineries";
//   //           toast.info(getLabel("toast_missingWineries"), {
//   //             toastId: customIdNoWineries,
//   //             position: toast.POSITION.BOTTOM_RIGHT,
//   //             exclude: true,
//   //             autoClose: 5000,
//   //           });
//   //           // return;
//   //         }
//   //         navigate("/#wineriesList");
//   //         setIsLoading(false);
//   //       } else {
//   //         navigate(location.pathname);
//   //         setIsLoading(false);
//   //       }
//   //     }
//   //   }

//   //   if (!auth?.user_id || !activeWinery) return;

//   //   // socket connection
//   //   const username = auth.username;
//   //   const client_name = auth.client_name;

//   //   socket.auth = {
//   //     username,
//   //     id: auth?.user_id,
//   //     client_name: client_name,
//   //     wineryId: activeWinery?.id,
//   //   };
//   //   socket.auth = {
//   //     token: auth?.accessToken,  // ✅ JWT token sicuro
//   //     wineryId: activeWinery?.id,
//   //   };

//   //   const sessionId = getItem("sessionId");
//   //   if (sessionId) {
//   //     socket.auth = { ...socket.auth, sessionId };
//   //   }

//   //   if (!socket.connected) {
//   //     socket.connect();
//   //   }

//   //   return () => (isMounted = false);
//   // }, [auth?.user_id, activeWinery?.id]);

//   useEffect(() => {
//     let isMounted = true;

//     const verifyRefreshToken = async () => {
//       try {
//         await refresh();
//       } catch (err) {
//         console.error(err);
//       } finally {
//         // ... tuo codice di navigazione esistente ...
//         isMounted && setIsLoading(false);
//       }
//     };

//     if (!auth?.accessToken) {
//       verifyRefreshToken();
//     } else {
//       // ... tua logica di navigazione esistente ...
//     }

//     if (!auth?.user_id || !activeWinery || !auth?.accessToken) return;  // ✅ Aggiunto check accessToken

//     // ✅ NUOVO: socket connection con JWT
//     socket.auth = {
//       token: auth?.accessToken,  // ✅ Passa il token JWT
//       wineryId: activeWinery?.id,
//     };

//     // ⚠️ RIMUOVI: Non serve più passare sessionId nell'auth (il token contiene tutto)
//     // const sessionId = getItem("sessionId");
//     // if (sessionId) {
//     //   socket.auth = { ...socket.auth, sessionId };
//     // }

//     if (!socket.connected) {
//       socket.connect();
//     }

//     return () => (isMounted = false);
//   }, [auth?.user_id, auth?.accessToken, activeWinery?.id]);  // ✅ Aggiunto auth?.accessToken alle dipendenze

//   useEffect(() => {
//     const delay = 5000;
//     const timeout = setTimeout(() => {
//       if (!ready) {
//         return;
//       }

//       // Fetch notifications
//       socket.emit("fetch notifications");

//       // Listen for notifications
//     }, delay);

//     return () => clearTimeout(timeout);
//   }, [ready]);

//   // socket.io
//   useEffect(() => {
//     // function onConnect() {
//     //   console.log("connect");
//     //   setIsConnected(true);
//     //   reconnecting = false; // reset flag quando connesso

//     //   // Re-register user after reconnect
//     //   socket.emit("register user", {
//     //     id: auth?.user_id,
//     //     username: auth?.username,
//     //     client_name: auth?.client_name,
//     //     wineryId: activeWinery?.id,
//     //     sessionId: getItem("sessionId"),
//     //   });
//     // }

//     function onConnect() {
//       console.log("✅ Socket connected");
//       setIsConnected(true);
//       reconnecting = false;

//       // ✅ NUOVO: Non serve più "register user" - il token contiene già i dati
//       // Il backend estrae userId, username, client_name dal JWT

//       // ⚠️ RIMUOVI questo:
//       // socket.emit("register user", {
//       //   id: auth?.user_id,
//       //   username: auth?.username,
//       //   client_name: auth?.client_name,
//       //   wineryId: activeWinery?.id,
//       //   sessionId: getItem("sessionId"),
//       // });
//     }

//     function onReconnectFailed() {
//       console.log("Reconnection failed");
//     }

//     function onReconnectAttempt() {
//       console.log("Reconnect attempt");

//       // ✅ AGGIORNA con il token attuale
//       socket.auth = {
//         token: auth?.accessToken,
//         wineryId: activeWinery?.id,
//       };
//     }

//     let reconnecting = false;

//     function onDisconnect(reason) {
//       console.log("disconnect", reason);
//       setIsConnected(false);

//       if (reason === "io server disconnect" && !reconnecting && auth?.user_id) {
//         reconnecting = true;

//         // ✅ AGGIORNA con il token
//         socket.auth = {
//           token: auth?.accessToken,
//           wineryId: activeWinery?.id,
//         };

//         socket.connect();
//       }

//       // ... resto del tuo codice esistente per userSockets ...
//     }

//     // function onReconnectAttempt() {
//     //   // On reconnect attempt (after server restart)
//     //   console.log("Reconnect attempt");
//     //   socket.auth = {
//     //     username: auth.username,
//     //     id: auth?.user_id,
//     //     client_name: auth.client_name,
//     //     wineryId: activeWinery?.id,
//     //     sessionId: getItem("sessionId"),
//     //   };
//     // }

//     // let reconnecting = false;

//     // function onDisconnect(reason) {
//     //   console.log("disconnect");
//     //   setIsConnected(false);

//     //   if (reason === "io server disconnect" && !reconnecting && auth?.user_id) {
//     //     reconnecting = true;
//     //     // Il server ha forzato la disconnessione: es. restart
//     //     // Serve aggiornare auth PRIMA di riconnettere
//     //     socket.auth = {
//     //       username: auth.username,
//     //       id: auth?.user_id,
//     //       client_name: auth.client_name,
//     //       wineryId: activeWinery?.id,
//     //       sessionId: getItem("sessionId"),
//     //     };
//     //     socket.connect();
//     //   }

//     //   const index = userSockets?.findIndex((u) => u?.self);

//     //   if (index === -1) {
//     //     return;
//     //   }
//     //   dispatch(
//     //     setUserSockets((prevState) => [
//     //       ...prevState?.slice(0, index),
//     //       {
//     //         ...prevState[index],
//     //         connected: false,
//     //         ws: undefined, // remove ws property because is not serializable
//     //       },
//     //       ...prevState?.slice(index + 1),
//     //     ])
//     //   );
//     // }

//     // function onConnectError(err) {
//     //   console.log("onConnectError", err);

//     //   if (err.message === "invalid username") {
//     //     console.log("usernameAlreadySelected", err);
//     //   }
//     //   // console.log(auth);

//     //   // retry connection
//     //   setTimeout(() => {
//     //     // socket connection
//     //     const username = auth.username;
//     //     const client_name = auth.client_name;
//     //     socket.auth = {
//     //       username,
//     //       id: auth?.user_id,
//     //       client_name: client_name,
//     //       wineryId: activeWinery?.id,
//     //     };
//     //     const sessionId = getItem("sessionId");
//     //     if (sessionId) {
//     //       socket.auth = { ...socket.auth, sessionId };
//     //     }
//     //     console.log("onConnectError timeout");

//     //     socket.connect();
//     //   }, 5000);
//     // }

//     function onConnectError(err) {
//       console.error("onConnectError", err);

//       // ✅ NUOVO: Gestisci errore di autenticazione JWT
//       // if (err.message === "Authentication error") {
//       //   console.error("❌ JWT authentication failed - token invalid or expired");

//       //   // Prova a refreshare il token
//       //   refresh()
//       //     .then(() => {
//       //       console.log("✅ Token refreshed, reconnecting...");

//       //       // Aggiorna auth con il nuovo token
//       //       socket.auth = {
//       //         token: auth?.accessToken,
//       //         wineryId: activeWinery?.id,
//       //       };

//       //       socket.connect();
//       //     })
//       //     .catch((refreshErr) => {
//       //       console.error("❌ Token refresh failed:", refreshErr);

//       //       // Logout e redirect al login
//       //       dispatch(logout());
//       //       navigate("/login");

//       //       toast.error(getLabel("toast_sessionExpired"), {
//       //         position: toast.POSITION.BOTTOM_RIGHT,
//       //         autoClose: 5000,
//       //       });
//       //     });

//       //   return; // ✅ Non continuare con il retry automatico sotto
//       // }

//       if (err.message === "invalid username") {
//         console.log("usernameAlreadySelected", err);
//       }

//       // Retry connection (solo per altri tipi di errori)
//       setTimeout(() => {
//         socket.auth = {
//           token: auth?.accessToken,  // ✅ Usa il token
//           wineryId: activeWinery?.id,
//         };

//         console.log("Retrying connection after error...");
//         socket.connect();
//       }, 5000);
//     }

//     // function onSession({ sessionId, userId }) {
//     //   // attach the session ID to the next reconnection attempts
//     //   socket.auth = { sessionId };
//     //   // store it in the localStorage
//     //   setItem("sessionId", sessionId);
//     //   // save the ID of the user
//     //   socket.userId = userId;
//     //   // console.log("session =============================================");
//     // }

//     function onUsers(users) {
//       // console.log("users =============================================", users);
//       const userSocketsTmp = [...userSockets];

//       for (const user of users) {
//         user.notifications?.forEach((notification) => {
//           notification.fromSelf = notification.from === socket.userID;
//           if (!notification.fromSelf) {
//             // onNotificationEvent(notification);
//           }
//         });

//         const userIndex = userSocketsTmp?.findIndex((u) => u?.id === user?.id);
//         if (userIndex === -1) {
//           // add new connected user
//           user.self = user.userID === socket.userID;
//           userSocketsTmp.push(user);
//         } else {
//           // update userId
//           const tmpUser = { ...userSocketsTmp[userIndex], ws: undefined };
//           userSocketsTmp[userIndex] = tmpUser;
//         }
//       }

//       // put the current user first, and sort by username
//       userSocketsTmp?.sort((a, b) => {
//         if (a.self) return -1;
//         if (b.self) return 1;
//         if (a.username < b.username) return -1;
//         return a.username > b.username ? 1 : 0;
//       });

//       dispatch(setUserSockets(userSocketsTmp));
//       // console.log(userSocketsTmp, "userSockets");
//     }

//     function onUserConnected(user) {
//       const userSocketsTmp = [...userSockets];

//       const userIndex = userSocketsTmp?.findIndex((u) => u?.id === user?.id);
//       if (userIndex === -1) {
//         // add new connected user
//         user.self = user.userID === socket.userID;
//         user.connected = true;
//         userSocketsTmp.push(user);
//       } else {
//         // update userId
//         const tmpUser = { ...userSocketsTmp[userIndex] };
//         tmpUser.connected = true;
//         userSocketsTmp[userIndex] = tmpUser;
//       }
//       // console.log("userSockets user connected");
//     }

//     function onUserDisonnected(id) {
//       const userSocketsTmp = [...userSockets];

//       const userIndex = userSocketsTmp?.findIndex((u) => u?.id === id);
//       if (userIndex > -1) {
//         // update userId
//         const tmpUser = { ...userSocketsTmp[userIndex] };
//         tmpUser.connected = false;
//         userSocketsTmp[userIndex] = tmpUser;
//       }
//       // console.log("userSockets user disconnected");
//     }

//     socket.on("connect", onConnect);
//     socket.on("disconnect", onDisconnect);
//     socket.on("reconnect_attempt", onReconnectAttempt);
//     socket.on("reconnect_failed", onReconnectFailed);
//     // socket.on("session", onSession);
//     socket.on("users", onUsers);
//     socket.on("user connected", onUserConnected);
//     socket.on("user disconnected", onUserDisonnected);
//     socket.on("connect_error", onConnectError);

//     return () => {
//       // socket.disconnect();

//       socket.off("connect", onConnect);
//       socket.off("disconnect", onDisconnect);
//       socket.off("reconnect_attempt", onReconnectAttempt);
//       socket.off("reconnect_failed", onReconnectFailed);
//       // socket.off("session", onSession);
//       socket.off("users", onUsers);
//       socket.off("user connected", onUserConnected);
//       socket.off("user disconnected", onUserDisonnected);
//       socket.off("connect_error", onConnectError);
//     };
//   }, []);

//   return (
//     <>
//       {isLoading ? (
//         <Loader />
//       ) : (
//         <>
//           <Header isMobile={isMobile} isConnected={isConnected}></Header>
//           <main className={`container ${isIcon ? "icon" : ""}`}>
//             <SideBar
//               isMobile={isMobile}
//               isIcon={isIcon}
//               setIsIcon={setIsIcon}
//             ></SideBar>
//             <ToastManager />
//             <NotesButton />
//             <Outlet />
//           </main>
//           <Footer isMobile={isMobile}></Footer>
//           <CookieBar isMobile={isMobile}></CookieBar>
//         </>
//       )}
//     </>
//   );
// };

// export default PersistentLogin;
